import { getTileLayerFeatures } from './decodeMVT'
import geoutils from './helper/geo'
import { TileAddress, TilesetConfig } from './types'

export type TileConfig = {
  address: TileAddress
  box: [number, number, number, number]
}

export class TilesetService {
  static findTileBounds({ x, y, z }: TileAddress) {
    return [
      geoutils.xToLon(x, z),
      geoutils.yToLat(y, z),
      geoutils.xToLon(x + 1, z),
      geoutils.yToLat(y + 1, z),
    ]
  }

  static urlForTile(tilesetConfig: TilesetConfig, address: TileAddress) {
    const { x, y, z } = address

    let tileUrl
    if (tilesetConfig.tileUrl.includes('{quadkey}')) {
      const quadKey = geoutils.tileXYToQuadKey(x, y, z)
      tileUrl = tilesetConfig.tileUrl.replace('{quadkey}', quadKey)
    } else {
      tileUrl = tilesetConfig.tileUrl
        .replace('{z}', `${z}`)
        .replace('{x}', `${x}`)
        .replace('{y}', `${y}`)
    }

    if (tilesetConfig.tileSubdomains && tilesetConfig.tileSubdomains.length > 0) {
      // replace subdomain with random domain from tileSubdomains array
      tileUrl = tileUrl.replace(
        '{s}',
        tilesetConfig.tileSubdomains[
          Math.floor(Math.random() * tilesetConfig.tileSubdomains.length)
        ]
      )
    }

    return tileUrl
  }

  static getChildBounds(
    parentAddress: TileAddress,
    childAddress: TileAddress
  ): { x: number; y: number; dimension: number } {
    if (parentAddress.z > childAddress.z) throw new Error('parent must include child')
    if (parentAddress.z === childAddress.z) return { x: 0, y: 0, dimension: 1 }

    const divisions = 2 ** (childAddress.z - parentAddress.z)

    const baseAddressAtChildZ = {
      x: parentAddress.x * divisions,
      y: parentAddress.y * divisions,
      z: childAddress.z,
    }

    const xOffset = childAddress.x - baseAddressAtChildZ.x
    const yOffset = childAddress.y - baseAddressAtChildZ.y

    const dimension = 1 / divisions

    return {
      x: xOffset * dimension,
      y: yOffset * dimension,
      dimension,
    }
  }

  /**
   *  Fetching tile from endpoint
   */
  static async getTile(tilesetConfig: TilesetConfig, tileConfig: TileConfig) {
    const { address } = tileConfig
    const { x, y, z } = address

    const { lonToX, latToY, xToLon, yToLat } = geoutils
    const maxZoom = tilesetConfig.zoomRange.max

    // cut from parent raster
    if (tilesetConfig.type !== 'vector' && z > maxZoom) {
      const parentAddress = {
        x: Math.floor(lonToX(xToLon(x, z), maxZoom)),
        y: Math.floor(latToY(yToLat(y, z), maxZoom)),
        z: maxZoom,
      }

      const tileUrl = TilesetService.urlForTile(tilesetConfig, parentAddress)

      const childBounds = TilesetService.getChildBounds(parentAddress, address)

      try {
        const canvas = await canvasForImageUrl(tileUrl)

        const { tileSize } = tilesetConfig

        const cropped = croppedCanvas(canvas, [
          Math.floor(childBounds.x * tileSize),
          Math.floor(childBounds.y * tileSize),
          Math.floor((childBounds.x + childBounds.dimension) * tileSize),
          Math.floor((childBounds.y + childBounds.dimension) * tileSize),
        ])

        const resized = resizeOffscreenCanvas(cropped, tileSize, tileSize)

        return {
          success: true,
          tile: {
            url: tileUrl,
            box: tileConfig.box,
            image: resized,
          },
        } as RasterTileSuccessResult
      } catch (error) {
        return {
          success: false,
          error,
        } as TileError
      }
    }

    const tileUrl = TilesetService.urlForTile(tilesetConfig, address)

    if (tilesetConfig.type === 'vector') {
      const res = await fetch(tileUrl)

      if (!res.ok) throw new Error(`Failed to fetch tile: ${res.statusText}`)

      const features = getTileLayerFeatures(
        await res.arrayBuffer(),
        tilesetConfig.layerName,
        address
      )

      return {
        success: true,
        tile: {
          url: tileUrl,
          box: tileConfig.box,
          features,
        },
      } as VectorTileSuccessResult
    }

    // raster tile
    try {
      const canvas = await canvasForImageUrl(tileUrl)

      return {
        success: true,
        tile: {
          url: tileUrl,
          box: tileConfig.box,
          image: canvas,
        },
      } as RasterTileSuccessResult
    } catch (error) {
      return {
        success: false,
        error,
      } as TileError
    }
  }

  /**
   *  Fetching tiles and limit concurrent connections
   */
  static async getTiles(tilesetConfig: TilesetConfig, tileConfigs: TileConfig[]) {
    const tilePromises: Promise<TileResult>[] = []
    tileConfigs.forEach(tileConfig => {
      tilePromises.push(TilesetService.getTile(tilesetConfig, tileConfig))
    })
    return Promise.all(tilePromises)
  }
}

export type VectorTileSuccessResult = {
  success: true
  tile: {
    url: string
    box: [number, number, number, number]
    features: any[]
  }
}
export type RasterTileSuccessResult = {
  success: true
  tile: {
    url: string
    box: [number, number, number, number]
    image: OffscreenCanvas
  }
}

export type TileError = {
  success: false
  error: any
}

type TileResult = VectorTileSuccessResult | RasterTileSuccessResult | TileError

const canvasForImageUrl = async (url: string) => {
  const response = await fetch(url)
  const blob = await response.blob()
  const imageBitmap = await createImageBitmap(blob)
  const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height)

  // Set OffscreenCanvas dimensions to match the image
  canvas.width = imageBitmap.width
  canvas.height = imageBitmap.height

  // Draw the ImageBitmap onto the OffscreenCanvas
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas

  ctx.drawImage(imageBitmap, 0, 0)

  return canvas
}

export const croppedCanvas = (
  sourceCanvas: OffscreenCanvas,
  box: [number, number, number, number]
) => {
  const [left, top, right, bottom] = box
  const width = right - left
  const height = bottom - top

  const cropped = new OffscreenCanvas(width, height)
  const ctx = cropped.getContext('2d')
  if (!ctx) throw new Error('Failed to get canvas context')

  ctx.drawImage(sourceCanvas, left, top, width, height, 0, 0, width, height)

  return cropped
}

/**
 * Resize an OffscreenCanvas
 * @param {OffscreenCanvas} canvas - source canvas
 * @param {number} width - target width
 * @param {number} height - target height
 * @returns {OffscreenCanvas} resized canvas
 */
function resizeOffscreenCanvas(canvas: OffscreenCanvas, width: number, height: number) {
  const resized = new OffscreenCanvas(width, height)
  const ctx = resized.getContext('2d')
  if (!ctx) throw new Error('Failed to get canvas context')

  ctx.imageSmoothingEnabled = false

  ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, width, height)

  return resized
}
